import { gql, useQuery } from '@apollo/client';
import Button from '@orn/shared-web/components/Button/Button';
import Input, { InputBase, InputGroup } from '@orn/shared-web/components/Input';
import { useClipboard } from '@orn/shared-web/hooks/useClipboard';
import { CheckIcon, CopyIcon } from '@orn/shared-web/icons';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import { useUpdateFamily } from '../../../hooks/mutations/useUpdateFamily';
import useAutoCommit from '../../../hooks/useAutoCommit';
import { SettingsCard } from '../common';

import type {
  GetFamilyInformationQuery,
  GetFamilyInformationQueryVariables,
} from './__generated__/FamilyInformationForm.gql';

// ---------------------------------------------------------------------------
// Base (presentational) — renders the UI from props + callbacks.
// ---------------------------------------------------------------------------

export interface IFamilyInformationFormBaseProps {
  name: string;
  onNameChange: (value: string) => void;
  saveError?: string;
  /** Disables the field while the household is still loading. */
  isLoading?: boolean;
  /**
   * The household could not be loaded. Replaces the field with the message and
   * a retry, rather than leaving an input the user cannot type into.
   */
  loadError?: string;
  onRetryLoad?: () => void;
  /** Persist the pending edit — wired to the input's blur. */
  onCommit: () => void;
  /**
   * The household's inbound email address. Absent on a deployment with no
   * assistant domain, so the row is dropped rather than shown empty — an
   * address field with nothing in it reads as something the user failed to set.
   */
  inboundEmailAddress?: string | null;
}

/**
 * Presentational "Family Information" settings card: the household's display
 * name, persisted via `onCommit` on blur — there is no save button, matching
 * the sibling `PersonalSettingsForm`. Fully controlled; the
 * `FamilyInformationForm` wrapper (below) owns the query and the mutation.
 */
export const FamilyInformationFormBase = ({
  name,
  onNameChange,
  saveError,
  isLoading = false,
  loadError,
  onRetryLoad,
  onCommit,
  inboundEmailAddress,
}: IFamilyInformationFormBaseProps) => {
  const { copied, copy } = useClipboard();

  return (
    <div className="flex w-full max-w-[736px] flex-col gap-2">
      <SettingsCard title="Family Information" subtitle="Your family details.">
        {loadError ? (
          <div className="flex flex-col items-start gap-3">
            <span className="text-error-primary text-sm">{loadError}</span>
            {onRetryLoad && (
              <Button color="secondary" size="sm" onClick={onRetryLoad}>
                Try again
              </Button>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <Input
              label="Family name"
              value={name}
              onChange={onNameChange}
              onBlur={onCommit}
              size="md"
              isDisabled={isLoading}
              hint="What we'll call your household in the app and in emails"
            />
            {inboundEmailAddress && (
              <InputGroup
                label="Email documents to"
                value={inboundEmailAddress}
                isReadOnly
                size="md"
                hint="You can forward bills or receipts to this email address"
                trailingAddon={
                  <Button
                    color="secondary"
                    aria-label={copied ? 'Address copied' : 'Copy address'}
                    iconLeading={copied ? CheckIcon : CopyIcon}
                    onClick={() => void copy(inboundEmailAddress)}
                  >
                    {/* The address is the thing worth reading, and the label
                        costs it about a dozen characters on a narrow screen —
                        enough to truncate the domain. */}
                    <span className="hidden sm:inline">{copied ? 'Copied' : 'Copy'}</span>
                  </Button>
                }
              >
                <InputBase />
              </InputGroup>
            )}
          </div>
        )}
      </SettingsCard>
      {saveError && <span className="text-error-primary text-sm">{saveError}</span>}
    </div>
  );
};

FamilyInformationFormBase.displayName = 'FamilyInformationFormBase';

// ---------------------------------------------------------------------------
// Wrapper (data) — fetches the family + sets up the mutation.
// ---------------------------------------------------------------------------

const GET_FAMILY_INFORMATION = gql`
  query GetFamilyInformation {
    getCurrentFamily {
      id
      name
      inboundEmailAddress
    }
  }
`;

/**
 * "Family Information" settings card wrapper. Fetches the caller's household
 * and owns the editable name. The name is seeded at signup from the caregiver's
 * surname, so this is usually an edit rather than a first entry; a household
 * whose caregiver had no surname starts empty.
 *
 * Blur with a blank value reverts to the saved name instead of saving. The
 * controller rejects an empty name, so committing one would surface an error
 * for what is almost always an accidental blur rather than an intent to clear.
 */
const FamilyInformationForm = () => {
  const { data, loading, error, refetch } = useQuery<GetFamilyInformationQuery, GetFamilyInformationQueryVariables>(
    GET_FAMILY_INFORMATION
  );
  const family = data?.getCurrentFamily;
  const savedName = family?.name ?? '';

  const [updateFamily] = useUpdateFamily();
  const [name, setName] = React.useState('');
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [hydrated, setHydrated] = React.useState(false);

  // Seed once when data first arrives. Never re-seed on later cache changes — a
  // blur-save's response landing while the user is still typing would clobber it.
  React.useEffect(() => {
    if (!family || hydrated) {
      return;
    }
    setName(family.name ?? '');
    setHydrated(true);
  }, [family, hydrated]);

  const commit = async () => {
    const trimmed = name.trim();
    if (trimmed === '') {
      setName(savedName);
      return;
    }
    if (trimmed === savedName) {
      return;
    }
    setSaveError(null);
    try {
      await updateFamily({ variables: { input: { name: trimmed } } });
    } catch (error) {
      logger.error('Failed to save family name', error);
      setSaveError("Couldn't save your changes. Please try again.");
    }
  };

  const requestCommit = useAutoCommit(commit);

  // A failed load and a slow one are different states, and conflating them
  // leaves the field disabled forever while still claiming to be loading. Only
  // an in-flight request is `isLoading`; a settled request that produced no
  // household is an error the user can retry.
  const loadFailed = !loading && !hydrated && (error !== undefined || family == null);

  // Renders the card immediately with the field disabled rather than a
  // `SettingsTabLoading` spinner: `PersonalSettingsForm` sits directly below and
  // shows its own, and that one is `min-h-[60vh]` — two of them stack into a
  // screen and a half of spinners on a tab that is about to show two cards.
  return (
    <FamilyInformationFormBase
      name={name}
      onNameChange={setName}
      saveError={saveError ?? undefined}
      isLoading={loading || (!hydrated && !loadFailed)}
      loadError={loadFailed ? "Couldn't load your family details." : undefined}
      onRetryLoad={() => void refetch()}
      onCommit={requestCommit}
      inboundEmailAddress={family?.inboundEmailAddress}
    />
  );
};

FamilyInformationForm.displayName = 'FamilyInformationForm';

export default FamilyInformationForm;
